import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { settings } from '../../settings/environment';
import { notifyAndTrackError } from '../common/error';
import { AppInsightsService } from './app-insights.service';
import { IAppUser } from './insecta';
import { AppUserService } from './insecta/app-user.service';
import { PermissionsService } from './insecta/permissions.service';

export interface IInsectaPermissions {
  canViewInsectaryDashboard: boolean;
  canViewRequestorDashboard: boolean;
  canViewMetrics: boolean;
  hasAnyPermissions: boolean;
  configureAdminGlobalLists: boolean;
  configureContainers: boolean;
  configureContainerTypes: boolean;
  configureContainmentTypes: boolean;
  configureGrowthStages: boolean;
  configureInsects: boolean;
  configureLocations: boolean;
  configureLocationTypes: boolean;
  configureSecurity: boolean;
  configurePermits: boolean;
  configureProjects: boolean;
  configureSiteCatalogs: boolean;
  configureSubstrates: boolean;
  configureUnitsOfMeasure: boolean;
  configureVariantTypes: boolean;
  configureVendors: boolean;
  insectaryApproveOrders: boolean;
  insectaryVendorOrders: boolean;
  insectaryInsectPrep: boolean;
  insectarySubstratePrep: boolean;
}

@Injectable({ providedIn: 'root' })
export class AppInitService {
  roles: string[] = [];
  userAccount: AccountInfo | null = null;
  appUser!: IAppUser;

  private static readonly ALL_INSECTA_PERMISSIONS: (keyof IInsectaPermissions)[] = [
    'canViewInsectaryDashboard',
    'canViewRequestorDashboard',
    'canViewMetrics',
    'hasAnyPermissions',
    'configureAdminGlobalLists',
    'configureContainers',
    'configureSecurity',
    'configureSubstrates',
    'configureSiteCatalogs',
    'configureContainerTypes',
    'configureContainmentTypes',
    'configureGrowthStages',
    'configureInsects',
    'configureLocationTypes',
    'configureLocations',
    'configurePermits',
    'configureProjects',
    'configureUnitsOfMeasure',
    'configureVariantTypes',
    'configureVendors',
    'insectaryApproveOrders',
    'insectaryVendorOrders',
    'insectaryInsectPrep',
    'insectarySubstratePrep',
  ];

  private static readonly CONFIG_ADMIN_GLOBAL_LISTS: (keyof IInsectaPermissions)[] = [
    'configureContainers',
    'configureSecurity',
    'configureSubstrates',
    'configureSiteCatalogs',
    'configureContainerTypes',
    'configureContainmentTypes',
    'configureGrowthStages',
    'configureInsects',
    'configureLocationTypes',
    'configureLocations',
    'configurePermits',
    'configureProjects',
    'configureUnitsOfMeasure',
    'configureVariantTypes',
    'configureVendors'
  ];

  permissions: IInsectaPermissions = {} as IInsectaPermissions;

  constructor(
    private msalService: MsalService,
    private router: Router,
    private permissionService: PermissionsService,
    private appInsights: AppInsightsService,
    private msgService: MessageService,
    private appUserService: AppUserService
  ) {}

  async initApp(): Promise<void> {
    // Initialize MSAL
    await this.msalService.instance.initialize();

    // get current environment
    const env = settings.environment.toLowerCase();

    // if user has logged out and is redirected after log out, do not login redirect
    const path = window.location.pathname.toLowerCase();
    if (path.includes('/after-logout')) {
      this.msalService.instance.clearCache();
      sessionStorage.clear();
      localStorage.clear();
      return;
    }

    // capture URL user was going to
    if (!path.includes('/after-login') && !path.includes('/after-logout') && !path.includes('/before-login')) {
      const attemptedUrl = window.location.pathname + window.location.search;
      sessionStorage.setItem('redirectPath', attemptedUrl);
    }

    // Handle redirect response from MSAL
    const result = await this.msalService.instance.handleRedirectPromise();
    if (result?.account) {
      this.msalService.instance.setActiveAccount(result.account);
    }

    // if user not logged in, then redirect for login
    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      this.msalService.loginRedirect();
      return;
    }

    // Extract roles from ID token claims
    const roles = (account.idTokenClaims?.roles ?? []) as string[];
    this.roles = roles;

    // Filter roles based on environment
    let suffix = '';
    if (['local', 'dev', 'test'].includes(env)) {
      suffix = 'DEVTEST';
    } else if (env === 'uat') {
      suffix = 'UAT';
    } else if (env === 'prod') {
      suffix = 'PROD';
    }

    const suffixToMatch = `-${suffix}`;
    const filteredRoles = roles.filter((role) => role.endsWith(suffixToMatch));

    // if user has no env specific roles, then redirect them to No Permissions page
    if (filteredRoles.length === 0) {
      this.router.navigate(['/no-permissions']);
      return;
    }

    // Strip environment suffix to get base roles - to match up with DB roles
    this.roles = filteredRoles.map((role) => role.replace(suffixToMatch, ''));

    // Get permissions from API based on Role(s)
    try {
      this.appUser = await firstValueFrom(this.appUserService.getAppUserByEmail(account.username));

      const response = await firstValueFrom(this.permissionService.getRolePermissionsByRoles(this.roles));

      this.permissions = AppInitService.ALL_INSECTA_PERMISSIONS.reduce((acc, key) => {
        const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
        acc[key] = response.includes(pascalKey);
        return acc;
      }, {} as IInsectaPermissions);

      this.permissions.configureAdminGlobalLists = AppInitService.CONFIG_ADMIN_GLOBAL_LISTS.some((key) => this.permissions[key]);
      this.permissions.hasAnyPermissions = response.length > 0;

      const redirectPath = sessionStorage.getItem('redirectPath');
      if (redirectPath) {
        sessionStorage.removeItem('redirectPath');
      }

      // Check if a specific path was stored before the login redirect
      if (redirectPath && redirectPath !== '/' && redirectPath !== '') {
        // A specific path was stored. Navigate there now that auth is complete.
        this.router.navigateByUrl(redirectPath);
      } else {
        // No specific path was stored, meaning the user landed at the root.
        // Redirect them to their default dashboard based on permissions.
        if (this.permissions.canViewInsectaryDashboard) {
          this.router.navigate(['/insectarydashboard']);
        } else if (this.permissions.canViewRequestorDashboard) {
          this.router.navigate(['/requesterdashboard']);
        } else {
          this.router.navigate(['/no-permissions']);
        }
      }
    } catch (err) {
      notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'AppInitService', action: 'LoadUserPermissions' });
      sessionStorage.removeItem('redirectPath');
    }
  }
}
